import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useTenant } from '@/hooks/useTenant';
import type { Company } from '@/types';

export { COMPANY_SELECT_COLUMNS } from '@/providers/tenantContext';

export function useCompanyProfile() {
  const queryClient = useQueryClient();
  const { tenantId, company, isLoadingCompany, companyQueryKey } = useTenant();

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Company>) => {
      if (!tenantId) throw new Error('No company found');

      const { error } = await supabase
        .from('companies')
        .update(updates)
        .eq('id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyQueryKey });
      toast({ title: 'Company profile updated successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update company profile',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    data: company,
    isLoading: isLoadingCompany,
    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}
